import { Button, Chip, Input, Spinner } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Eye, EyeOff, GripVertical } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { RichTextField } from '@/components/editor/RichTextField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { MediaPicker } from '@/components/media/MediaPicker';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import type { MediaRow } from '@/routes/media/types';

import { SectionThumbnail } from './SectionThumbnail';
import type { PageKeySummary, PageSectionRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.2 — `/page-sections/:pageKey`.
 *
 * "The most important screen for keeping the designed pages fresh without a
 * developer." Left: the page's sections in order, with visibility toggles and
 * reordering. Right: the selected section's form and its thumbnail.
 *
 * The section keys are not a constant here. They come from the API, which reads
 * them from the rows — so a page added by a later migration appears on this
 * screen without this file changing, and a key removed from the components
 * stops appearing without it going stale.
 */
export function PageSectionsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <PageList /> : <Sections pageKey={rest} />;
}

const KEY = ['admin', 'page-sections'];

function PageList() {
  const { t } = useTranslation();

  const pages = useQuery({
    queryKey: [...KEY, 'pages'],
    queryFn: () => apiFetch<{ data: PageKeySummary[] }>('/admin/page-sections/pages'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.pageSections')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('pageSections.intro')}</p>
      </div>

      {pages.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}
      {pages.isError ? <FormError message={t('errors.generic')} /> : null}

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(pages.data?.data ?? []).map((page) => (
          <li key={page.page_key}>
            <Link
              to={`/page-sections/${page.page_key}`}
              className="block rounded-card border border-subtle p-4 transition-colors hover:border-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            >
              <span className="block font-semibold text-primary">
                {t(`pageSections.pages.${page.page_key}`, page.page_key)}
              </span>

              <span className="mt-1 block text-caption text-muted">
                {t('pageSections.sectionCount', { count: page.sections })}
                {page.hidden > 0
                  ? ` · ${t('pageSections.hiddenCount', { count: page.hidden })}`
                  : ''}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Sections({ pageKey }: { pageKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sections = useQuery({
    queryKey: [...KEY, pageKey],
    queryFn: () =>
      apiFetch<{ data: PageSectionRow[] }>('/admin/page-sections', {
        query: { page_key: pageKey },
      }),
  });

  // Memoised so the effect below does not see a new array every render.
  const rows = useMemo(() => sections.data?.data ?? [], [sections.data]);
  const current = rows.find((row) => row.id === selected) ?? rows[0] ?? null;

  // The first section, once they arrive. Not in `useState`'s initialiser: the
  // list is empty on the first render and there is nothing to select yet.
  useEffect(() => {
    if (selected === null && rows.length > 0) {
      setSelected(rows[0]?.id ?? null);
    }
  }, [rows, selected]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [...KEY, pageKey] });
  };

  const fail = (cause: unknown): void => {
    setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
  };

  const save = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      apiFetch(`/admin/page-sections/${id}`, { method: 'PUT', body }),
    onSuccess: invalidate,
    onError: fail,
  });

  const reorder = useMutation({
    mutationFn: (ids: number[]) =>
      apiFetch('/admin/page-sections/reorder', {
        method: 'POST',
        body: { page_key: pageKey, ids },
      }),
    onSuccess: invalidate,
    onError: fail,
  });

  function move(index: number, delta: number): void {
    const target = index + delta;

    if (target < 0 || target >= rows.length) {
      return;
    }

    const ids = rows.map((row) => row.id);
    [ids[index], ids[target]] = [ids[target] as number, ids[index] as number];

    reorder.mutate(ids);
  }

  function drop(onto: number): void {
    if (dragging === null || dragging === onto) {
      return;
    }

    const ids = rows.map((row) => row.id);
    const [moved] = ids.splice(dragging, 1);

    ids.splice(onto, 0, moved as number);

    setDragging(null);
    reorder.mutate(ids);
  }

  if (sections.isPending) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">
            {t(`pageSections.pages.${pageKey}`, pageKey)}
          </h1>
          <p className="mt-1 text-body-sm text-secondary">{t('pageSections.pageIntro')}</p>
        </div>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/page-sections')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={error} />

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        {/* Left: the order of the page. */}
        <ol className="space-y-2">
          {rows.map((row, index) => (
            <li
              key={row.id}
              draggable
              onDragStart={() => setDragging(index)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => drop(index)}
              className={[
                'flex items-center gap-1 rounded-card border p-2',
                row.id === current?.id ? 'bg-surface-subtle border-navy-600' : 'border-subtle',
                /*
                 * A dashed border rather than `opacity-60`.
                 *
                 * Dimming the row took the already-muted heading preview to
                 * 2.39:1 — `--text-muted` #696961 clears AA at 5.54:1 until 60%
                 * of it is composited away into #a2a29c on --bg-page — and took
                 * the four reorder buttons with it. §7.2 wants 4.5:1 on body
                 * text, and a hidden section is still text somebody reads to
                 * decide whether to unhide it. `MenusPage` had the same bug.
                 */
                row.is_visible ? '' : 'border-dashed',
              ].join(' ')}
            >
              <GripVertical
                size={14}
                strokeWidth={1.75}
                aria-hidden="true"
                className="shrink-0 text-muted"
              />

              <button
                type="button"
                aria-current={row.id === current?.id ? 'true' : undefined}
                className="min-w-0 flex-1 text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                onClick={() => setSelected(row.id)}
              >
                <span className="block truncate text-body-sm font-semibold text-primary">
                  {t(`pageSections.keys.${row.section_key}`, row.section_key)}
                </span>

                <span className="line-clamp-1 text-caption text-muted">
                  {valueFor(row.heading, locale) || '—'}
                </span>

                {/*
                  The same gold chip the form panel already uses for this state,
                  so the list and the panel say it the same way — and on its own
                  line rather than beside the name.
                  
                  Beside it, the chip won the space fight: this column is 320px
                  wide with a grip and three buttons already in it, so
                  "განცხადებები" truncated to "გ…" and the row lost the one
                  thing it exists to tell you. Only hidden rows grow, which is
                  the honest trade.
                */}
                {row.is_visible ? null : (
                  <span className="mt-1 inline-flex">
                    <Chip tone="gold">{t('pageSections.hidden')}</Chip>
                  </span>
                )}
              </button>

              {/*
                Buttons as well as dragging. §5.2 asks for drag-to-reorder and
                a pointer-only reorder is unusable by keyboard — these are the
                same operation, reachable both ways.
              */}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={t('pageSections.moveUp', { name: row.section_key })}
                disabled={index === 0 || reorder.isPending}
                onClick={() => move(index, -1)}
              >
                <ChevronUp size={14} strokeWidth={1.75} aria-hidden="true" />
              </Button>

              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={t('pageSections.moveDown', { name: row.section_key })}
                disabled={index === rows.length - 1 || reorder.isPending}
                onClick={() => move(index, 1)}
              >
                <ChevronDown size={14} strokeWidth={1.75} aria-hidden="true" />
              </Button>

              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-pressed={row.is_visible}
                aria-label={t(row.is_visible ? 'pageSections.hide' : 'pageSections.show', {
                  name: row.section_key,
                })}
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({
                    id: row.id,
                    // The whole payload back, unchanged: this endpoint is a
                    // PUT, and sending only `is_visible` would blank the
                    // heading and the section's data along with it.
                    body: {
                      heading: row.heading,
                      subheading: row.subheading,
                      data: row.data,
                      is_visible: !row.is_visible,
                      sort_order: row.sort_order,
                    },
                  })
                }
              >
                {row.is_visible ? (
                  <Eye size={14} strokeWidth={1.75} aria-hidden="true" />
                ) : (
                  <EyeOff size={14} strokeWidth={1.75} aria-hidden="true" />
                )}
              </Button>
            </li>
          ))}
        </ol>

        {/* Right: the selected section. */}
        {current ? (
          <SectionForm
            key={current.id}
            section={current}
            saving={save.isPending}
            onSave={(body) => save.mutate({ id: current.id, body })}
          />
        ) : (
          <p className="text-body-sm text-secondary">{t('pageSections.emptyPage')}</p>
        )}
      </div>
    </div>
  );
}

/**
 * The form for one section.
 *
 * §5.2 describes three kinds, and the third is the interesting one: "Sections
 * whose content comes from another resource (news grid, faculty cards, program
 * levels) expose only the heading, sub-heading, item count, and a link to the
 * source resource — they don't duplicate the data."
 */
function SectionForm({
  section,
  saving,
  onSave,
}: {
  section: PageSectionRow;
  saving: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();

  const [heading, setHeading] = useState<TranslatedValue>(section.heading);
  const [subheading, setSubheading] = useState<TranslatedValue>(section.subheading);
  const [data, setData] = useState<Record<string, unknown>>(section.data);

  /*
   * The images the section arrived with, plus any the editor has picked since.
   * Without the second half a freshly chosen photograph would leave an empty
   * frame until the page was saved and reloaded.
   */
  const [picked, setPicked] = useState<Record<number, MediaRow>>({});

  const media: Record<number, MediaRow> = { ...byId(section.media), ...picked };
  const remember = (row: MediaRow): void => setPicked((current) => ({ ...current, [row.id]: row }));

  const source = SOURCES[section.section_key];

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          heading,
          subheading,
          data,
          is_visible: section.is_visible,
          sort_order: section.sort_order,
        });
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-h4 font-extrabold text-primary">
          {t(`pageSections.keys.${section.section_key}`, section.section_key)}
        </h2>

        {section.is_visible ? null : <Chip tone="gold">{t('pageSections.hidden')}</Chip>}
      </div>

      {/* Live: it reads the values being edited, not the saved row. */}
      <SectionThumbnail section={{ ...section, heading, subheading, data }} />

      <TranslatableField
        label={t('pageSections.fields.heading')}
        name="heading"
        value={heading}
        onChange={setHeading}
        required={false}
      />

      <TranslatableField
        label={t('pageSections.fields.subheading')}
        name="subheading"
        multiline
        rows={2}
        value={subheading}
        onChange={setSubheading}
        required={false}
      />

      {section.section_key === 'hero' ? (
        <HeroFields data={data} media={media} onMediaPicked={remember} onChange={setData} />
      ) : source ? (
        <SourceFields data={data} source={source} onChange={setData} />
      ) : section.section_key === 'internationalisation' ? (
        <TabFields data={data} media={media} onMediaPicked={remember} onChange={setData} />
      ) : (
        // Every other key: heading and subheading are the whole of it, which
        // is true of the bands whose content is a fixed part of the design.
        <p className="text-caption text-muted">{t('pageSections.headingOnly')}</p>
      )}

      <Button type="submit" disabled={saving}>
        {saving ? t('common.saving') : t('common.save')}
      </Button>
    </form>
  );
}

/** §5.2's hero fields. */
function HeroFields({
  data,
  media,
  onMediaPicked,
  onChange,
}: {
  data: Record<string, unknown>;
  media: Record<number, MediaRow>;
  onMediaPicked: (media: MediaRow) => void;
  onChange: (data: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();

  const set = (key: string, value: unknown): void => onChange({ ...data, [key]: value });

  return (
    <div className="space-y-4 rounded-card border border-subtle p-4">
      {/*
        The hero photograph. It was a text box holding `background_path` — a key
        nothing on the public site ever read, so a change here did nothing at
        all — and the largest image on the page could not be chosen from the
        admin at all.
      */}
      <MediaPicker
        label={t('pageSections.fields.background')}
        hint={t('pageSections.fields.backgroundHint')}
        value={pickedImage(data, media)}
        onChange={(mediaId, row) => {
          if (row) {
            onMediaPicked(row);
          }

          set('media_id', mediaId);
        }}
      />

      <TranslatableField
        label={t('pageSections.fields.eyebrow')}
        name="eyebrow"
        value={translated(data, 'eyebrow')}
        onChange={(next) => set('eyebrow', next)}
        required={false}
      />

      <TranslatableField
        label={t('pageSections.fields.titleLine1')}
        name="title_line_1"
        value={translated(data, 'title_line_1')}
        onChange={(next) => set('title_line_1', next)}
        required={false}
      />

      {/* §5.2 — "title line 2 (gold)". Said out loud, because the form cannot
          show it and the thumbnail beside it can. */}
      <TranslatableField
        label={t('pageSections.fields.titleLine2')}
        hint={t('pageSections.fields.titleLine2Hint')}
        name="title_line_2"
        value={translated(data, 'title_line_2')}
        onChange={(next) => set('title_line_2', next)}
        required={false}
      />

      <RichTextField
        label={t('content.fields.body')}
        value={translated(data, 'body')}
        onChange={(next) => set('body', next)}
      />
    </div>
  );
}

/** §5.2's tabbed panel — the `internationalisation` key. */
function TabFields({
  data,
  media,
  onMediaPicked,
  onChange,
}: {
  data: Record<string, unknown>;
  media: Record<number, MediaRow>;
  onMediaPicked: (media: MediaRow) => void;
  onChange: (data: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();

  const tabs = Array.isArray(data.tabs) ? (data.tabs as Record<string, unknown>[]) : [];

  const update = (index: number, next: Record<string, unknown>): void =>
    onChange({ ...data, tabs: tabs.map((tab, position) => (position === index ? next : tab)) });

  return (
    <div className="space-y-3">
      {tabs.map((tab, index) => (
        <div key={index} className="space-y-3 rounded-card border border-subtle p-4">
          <TranslatableField
            label={t('pageSections.fields.tabHeading')}
            name={`tab-${index}-heading`}
            value={translated(tab, 'heading')}
            onChange={(next) => update(index, { ...tab, heading: next })}
            required={false}
          />

          <RichTextField
            label={t('content.fields.body')}
            value={translated(tab, 'body')}
            onChange={(next) => update(index, { ...tab, body: next })}
          />

          {/* §5.2's tabbed panel carries a picture per tab. */}
          <MediaPicker
            label={t('pageSections.fields.tabImage')}
            value={pickedImage(tab, media)}
            onChange={(mediaId, row) => {
              if (row) {
                onMediaPicked(row);
              }

              update(index, { ...tab, media_id: mediaId });
            }}
          />
        </div>
      ))}

      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={() => onChange({ ...data, tabs: [...tabs, { heading: {}, body: {} }] })}
      >
        {t('pageSections.addTab')}
      </Button>
    </div>
  );
}

/**
 * §5.2 — a section whose content is another resource's.
 *
 * "They don't duplicate the data": the only thing to set is how many rows to
 * draw, and the only other thing worth offering is a way to go and edit them.
 */
function SourceFields({
  data,
  source,
  onChange,
}: {
  data: Record<string, unknown>;
  source: string;
  onChange: (data: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-end gap-4 rounded-card border border-subtle p-4">
      <Input
        type="number"
        min={1}
        max={24}
        label={t('pageSections.fields.limit')}
        hint={t('pageSections.fields.limitHint')}
        value={typeof data.limit === 'number' ? String(data.limit) : ''}
        onChange={(event) =>
          onChange({
            ...data,
            limit: event.target.value === '' ? null : Number(event.target.value),
          })
        }
      />

      <Link
        to={`/${source}`}
        className="text-body-sm font-semibold text-navy-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
      >
        {t('pageSections.editSource', { name: t(`nav.items.${source}`) })}
      </Link>
    </div>
  );
}

/**
 * §7's section keys whose rows come from a resource, and which resource.
 *
 * A section key not in here gets the heading-only form, which is the right
 * default: a band whose content is part of the design has nothing else to set,
 * and a key added by a later migration should appear with something usable
 * rather than not appear at all.
 */
const SOURCES: Record<string, string> = {
  news: 'news',
  announcements: 'announcements',
  events: 'events',
  clubs: 'clubs',
  faculties: 'faculties',
  programs: 'programs',
  levels: 'programs',
};

function translated(data: Record<string, unknown>, key: string): TranslatedValue {
  const value = data[key];

  return typeof value === 'object' && value !== null ? (value as TranslatedValue) : {};
}

/** The picked image for a payload's `media_id`, or null while none is chosen. */
function pickedImage(
  data: Record<string, unknown>,
  media: Record<number, MediaRow>,
): MediaRow | null {
  const id = data.media_id;

  return typeof id === 'number' ? (media[id] ?? null) : null;
}

/** The list the API sends, as the map the pickers look up by `media_id`. */
function byId(media: MediaRow[]): Record<number, MediaRow> {
  const result: Record<number, MediaRow> = {};

  for (const row of media) {
    result[row.id] = row;
  }

  return result;
}
